import React from "react";
import { Link, useParams } from "react-router-dom";

// Re-use the same images you already have
import mortgagesImg from "../assets/loan-mortgages.jpg";
import firstHomeImg from "../assets/loan-first-home.png";
import investmentImg from "../assets/loan-investment.png";
import refinancingImg from "../assets/loan-refinancing.jpg";
import constructionImg from "../assets/loan-construction.jpg";
import commercialImg from "../assets/loan-commercial.jpg";

import lifeImg from "../assets/icon-life-insurance.jpg";
import incomeImg from "../assets/icon-income-protection.png";
import healthImg from "../assets/icon-health-trauma.png";
import mortgageImg from "../assets/icon-mortgage-protection.png";

import businessLoansImg from "../assets/business-loans.png";
import businessFinanceImg from "../assets/business-finance.png";
import equipmentFinanceImg from "../assets/equipment-finance.png";

const serviceDetails = {
  // LOANS
  "mortgages": {
    id: "mortgages",
    category: "Loans",
    title: "Mortgages",
    tagline: "Clear, independent home loan advice",
    description:
      "We compare home loans across the major New Zealand banks and specialist lenders, then work out how yours should be put together. Getting the structure right at the start is the part that saves money later, and it is the part nobody explains at the bank counter.",
    image: mortgagesImg,
    points: [
      "Comparison across major banks and specialist lenders.",
      "Loan structures built around your income, not the bank’s default.",
      "Fixed, floating, or split-loan strategy advice.",
      "Support through approval, settlement and later reviews.",
    ],
  },

  "first-home-buyers": {
    id: "first-home-buyers",
    category: "Loans",
    title: "First Home Buyers",
    tagline: "Buying your first place",
    description:
      "There is a lot to take in the first time round. We take you through budgeting, KiwiSaver, pre-approval and settlement, explaining each step when you reach it rather than dumping it all on you at once.",
    image: firstHomeImg,
    points: [
      "Help with deposits, KiwiSaver and the grants you may qualify for.",
      "Step-by-step explanation of the home buying process.",
      "Pre-approval support and lender negotiations.",
      "Plain answers instead of bank jargon.",
    ],
  },

  "investment-property": {
    id: "investment-property",
    category: "Loans",
    title: "Investment Property",
    tagline: "Finance for property investors",
    description:
      "First rental or fifth, the lending structure decides how much room you have to move later. We set yours up around cashflow and the amount of risk you are actually comfortable carrying.",
    image: investmentImg,
    points: [
      "Cashflow-focused lending structures.",
      "Advice on growing a portfolio and borrowing against equity.",
      "Interest-only options, with an eye on tax.",
      "Keeping your risk at a level you’re comfortable with.",
    ],
  },

  "refinancing": {
    id: "refinancing",
    category: "Loans",
    title: "Refinancing",
    tagline: "Review your loan and reduce interest",
    description:
      "Rates move, and banks compete hardest for new customers rather than existing ones. A loan nobody has looked at in three years is often not the best one on offer. We check yours and tell you whether moving is worth it.",
    image: refinancingImg,
    points: [
      "Review of current interest rates and structure.",
      "Negotiation with lenders for better terms.",
      "Debt consolidation options where suitable.",
      "More repayment flexibility where the numbers support it.",
    ],
  },

  "construction-loans": {
    id: "construction-loans",
    category: "Loans",
    title: "Construction Loans",
    tagline: "Finance for builds and renovations",
    description:
      "Finance for new builds, renovations and extensions. Construction lending releases money in stages against the build, so the paperwork is heavier and the timing matters more. We handle that side with the bank.",
    image: constructionImg,
    points: [
      "Finance for new builds and renovations.",
      "Progress payment and drawdown guidance.",
      "Support for turnkey and fixed-price builds.",
      "Coordination with banks and builders.",
    ],
  },

  "commercial-property": {
    id: "commercial-property",
    category: "Loans",
    title: "Commercial Property",
    tagline: "Lending for business premises",
    description:
      "Lending for premises you occupy yourself, and for commercial property you hold as an investment. Deposit requirements and terms differ from residential lending, so we set expectations early rather than at the last minute.",
    image: commercialImg,
    points: [
      "Owner-occupied and investment lending.",
      "Cashflow and lease-structure analysis.",
      "Lending set up around how the business trades.",
      "A structure that still works as the business grows.",
    ],
  },

  // PROTECTION
  "life-insurance": {
    id: "life-insurance",
    category: "Protection",
    title: "Life Insurance",
    tagline: "Cover for the people you leave behind",
    description:
      "A lump sum paid to your family if you die. It clears the debt and keeps the household running. The only real question is how much, and for how long it needs to last.",
    image: lifeImg,
    points: [
      "Cover for debts, living costs, and dependents.",
      "Cover sized to where you are in life.",
      "Comparison across leading insurers.",
      "Help with the claim when it is needed.",
    ],
  },

  "income-protection": {
    id: "income-protection",
    category: "Protection",
    title: "Income Protection",
    tagline: "Part of your pay if you can’t work",
    description:
      "If illness or injury stops you working, this pays part of your income until you are back on your feet. The mortgage, the rates and the groceries don’t stop just because you have.",
    image: incomeImg,
    points: [
      "Replaces part of your income each month.",
      "Waiting period and benefit term advice.",
      "Support for employees and self-employed clients.",
      "Cover you can keep paying for.",
    ],
  },

  "health-trauma": {
    id: "health-trauma",
    category: "Protection",
    title: "Health & Trauma",
    tagline: "A lump sum if you’re seriously ill",
    description:
      "A lump sum paid when you are diagnosed with a serious condition. It buys you time off work, and it covers treatment costs that tend to arrive faster than expected.",
    image: healthImg,
    points: [
      "A lump sum paid on diagnosis.",
      "Help covering medical and recovery costs.",
      "Straightforward policy explanations.",
      "Help with the claim at a bad time.",
    ],
  },

  "mortgage-protection": {
    id: "mortgage-protection",
    category: "Protection",
    title: "Mortgage Protection",
    tagline: "Repayments covered if you can’t work",
    description:
      "Covers your home loan repayments if you cannot work because of illness, injury or redundancy. What qualifies varies between policies, so we go through that section with you before you sign.",
    image: mortgageImg,
    points: [
      "Cover for mortgage repayments.",
      "Protection against illness, injury, or redundancy.",
      "Options for how and when it pays out.",
      "One less thing to sort out if your income stops.",
    ],
  },

  // BUSINESS
  "business-loans": {
    id: "business-loans",
    category: "Business",
    title: "Business Loans",
    tagline: "Funding for growth and acquisitions",
    description:
      "Loans to buy a business, buy into a partnership, or fund working capital. We structure the repayments around your trading cycle, so the loan does not create the problem it was meant to solve.",
    image: businessLoansImg,
    points: [
      "Funding for business growth and acquisitions.",
      "Cashflow-friendly repayment structures.",
      "Support for partnerships and buy-ins.",
      "Advice on the deal, not only the loan.",
    ],
  },

  "business-finance": {
    id: "business-finance",
    category: "Business",
    title: "Business Finance",
    tagline: "Funding to run and grow a business",
    description:
      "Funding for expansion and for day-to-day operations. We look at what the money is for and how quickly it comes back before recommending a structure.",
    image: businessFinanceImg,
    points: [
      "Funding for operations and expansion.",
      "Structures matched to what the money is for.",
      "Repayments that line up with your cashflow.",
      "Ongoing reviews as the business changes.",
    ],
  },

  "equipment-finance": {
    id: "equipment-finance",
    category: "Business",
    title: "Equipment Finance",
    tagline: "Upgrade equipment without cash pressure",
    description:
      "Finance for vehicles, machinery and plant. The equipment usually secures the loan itself, which keeps your cash in the business rather than parked in a depreciating asset.",
    image: equipmentFinanceImg,
    points: [
      "Finance for vehicles, machinery, and equipment.",
      "Keeps your working capital free.",
      "Flexible ownership and lease options.",
      "Approvals that do not hold up the purchase.",
    ],
  },
};

// Services where the visitor is likely wondering "how much?" - these get a
// link through to the calculator. Add an id here to show it on another page.
const CALCULATOR_SERVICES = ["refinancing", "mortgages", "first-home-buyers"];

const CATEGORY_HEADINGS = {
  Loans: "More lending we arrange",
  Protection: "Other types of cover",
  Business: "More business funding",
};

const ArrowIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 12h14" />
    <path d="M13 6l6 6-6 6" />
  </svg>
);

const CheckIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </svg>
);

function ServiceDetailPage() {
  const { serviceId } = useParams();
  const service = serviceDetails[serviceId];

  if (!service) {
    return (
      <div className="svc-page">
        <section className="svc-hero svc-hero-empty">
          <div className="svc-hero-inner">
            <div className="svc-hero-text">
              <h1 className="svc-hero-title">Service not found</h1>
              <p className="svc-hero-desc">
                That page is not here. It may have moved, or the link may be
                wrong.
              </p>
              <div className="svc-hero-actions">
                <Link to="/" className="pill-btn pill-btn-primary">
                  Back to the homepage
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  const related = Object.values(serviceDetails).filter(
    (item) => item.category === service.category && item.id !== service.id
  );

  return (
    <div className="svc-page">
      {/* Hero: everything someone needs to decide whether to get in touch */}
      <section className="svc-hero">
        <div className="svc-hero-inner">
          <div className="svc-hero-text">
            <span className="svc-hero-kicker">{service.category}</span>
            <h1 className="svc-hero-title">{service.title}</h1>
            <p className="svc-hero-tagline">{service.tagline}</p>
            <p className="svc-hero-desc">{service.description}</p>

            <div className="svc-hero-actions">
              <Link to="/contact" className="pill-btn pill-btn-primary">
                Book a free consultation
                <ArrowIcon />
              </Link>

              {/* Saves them hunting for the calculator in the nav */}
              {CALCULATOR_SERVICES.includes(serviceId) && (
                <Link to="/calculator" className="pill-btn pill-btn-ghost">
                  Try the calculator
                </Link>
              )}
            </div>
          </div>

          <div className="svc-hero-media">
            <img src={service.image} alt={service.title} />
          </div>
        </div>
      </section>

      {/* What's included */}
      <section className="svc-section">
        <div className="svc-section-head">
          <span className="svc-section-kicker">How we help</span>
          <h2 className="svc-section-title">What working with us covers</h2>
        </div>

        <ul className="svc-points">
          {service.points.map((point) => (
            <li key={point} className="svc-point">
              <span className="svc-point-icon">
                <CheckIcon />
              </span>
              <span>{point}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Other services in the same category */}
      {related.length > 0 && (
        <section className="svc-section svc-related-section">
          <div className="svc-section-head">
            <span className="svc-section-kicker">{service.category}</span>
            <h2 className="svc-section-title">
              {CATEGORY_HEADINGS[service.category] || "Related services"}
            </h2>
          </div>

          <div className="svc-related">
            {related.map((item) => (
              <Link
                key={item.id}
                to={`/services/${item.id}`}
                className="svc-related-card"
              >
                <span className="svc-related-image">
                  <img src={item.image} alt="" />
                </span>
                <span className="svc-related-body">
                  <span className="svc-related-title">{item.title}</span>
                  <span className="svc-related-tagline">{item.tagline}</span>
                  <span className="svc-related-more">
                    Learn more
                    <ArrowIcon />
                  </span>
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Closing band so the page ends on a next step, not the footer */}
      <section className="svc-section cta-band-section">
        <div className="cta-band">
          <div className="cta-band-text">
            <h2>Not sure where to start?</h2>
            <p>A quick chat is usually enough to work out your options.</p>
          </div>
          <div className="cta-band-actions">
            <Link to="/contact" className="pill-btn pill-btn-light">
              Book a free chat
              <ArrowIcon />
            </Link>
            <a href="tel:+6492176765" className="cta-band-phone">
              or call 09-2176765
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

export default ServiceDetailPage;
